import { type WorkflowAssignmentModel, WorkflowActivityComposeModel, type WorkflowStateModel } from "../../../models/workflow/workflow.models";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { I18N } from "aurelia-i18n";
import { WorkflowAssignmentsService } from "../../../services/workflow/workflow-assignments-service";
import { computedFrom } from "aurelia-binding";
import * as Enumerable from "linq";
import { Reflection } from "../../../utilities/reflection";
import { DialogServiceConstants } from "../../../constants/basics/dialog.constants";
import { VettingService } from '../../../services/vetting/vetting.service';

export class BaseWorkflowActivityAssignmentViewModel {

    _model: WorkflowActivityComposeModel;
    correspondenceEmails: any[];

    @computedFrom(Reflection.getPropertyPath<BaseWorkflowActivityAssignmentViewModel>(z => z._model))
    get item(): any {
        return this._model.item;
    }

    @computedFrom(Reflection.getPropertyPath<BaseWorkflowActivityAssignmentViewModel>(z => z._model))
    get reload(): ((workflowState: WorkflowStateModel, canTakeOwnership: boolean, canReassign: boolean) => void) {
        return this._model.reload;
    }

    @computedFrom(Reflection.getPropertyPath<BaseWorkflowActivityAssignmentViewModel>(z => z._model))
    get remarks(): string {
        return this._model.assignment.remarks;
    }

    @computedFrom(Reflection.getPropertyPath<BaseWorkflowActivityAssignmentViewModel>(z => z._model))
    get workflowState(): WorkflowStateModel {
        return this._model.workflowState;
    }

    @computedFrom(Reflection.getPropertyPath<BaseWorkflowActivityAssignmentViewModel>(z => z._model))
    get assignment(): WorkflowAssignmentModel {
        return this._model.assignment;
    }

    @computedFrom(Reflection.getPropertyPath<BaseWorkflowActivityAssignmentViewModel>(z => z._model))
    get entityTypeId(): number {
        return this._model.entityTypeId;
    }

    @computedFrom(Reflection.getPropertyPath<BaseWorkflowActivityAssignmentViewModel>(z => z._model))
    get isUpcomingAssignment(): boolean {
        return this._model.isUpcomingAssignment;
    }

    public isLastClosedAssignment: boolean;

    constructor(protected dialogClientService: DialogClientService,
        protected i18N: I18N,
        protected workflowAssignmentsService: WorkflowAssignmentsService,
        protected vetClientService: VettingService) { }

    async activate(model: WorkflowActivityComposeModel) {
        if (!model) {
            return;
        }

        this._model = model;

        this._model.entityTypeId =
            this._model.entityTypeId ? this._model.entityTypeId :
                (this._model.workflowState && this._model.workflowState.entityTypeId ? this._model.workflowState.entityTypeId : null);

        const orderedSteps = Enumerable.from(this.workflowState.workflowAssignments).where(x => x.closedDate != null).orderBy(x => x.stepNumber);
        this.isLastClosedAssignment = orderedSteps.lastOrDefault() == this.assignment;

        this.assignment.assignmentState.showUserAndDepartment = true;
        this.assignment.assignmentState.showStatus = true;
        this.assignment.assignmentState.isSystemAssignment = false;
        this.assignment.assignmentState.classList = '';
        this.assignment.assignmentState.actionTakerAnonText = this.i18N.tr("unknown-text");
        if (this.assignment.isCurrentAssignment) {
            await this.getCorrespondenceEmails();
        }
        this.onActivated();
    }

    public cancelWorkflow() {
        this.dialogClientService.show(DialogServiceConstants.URLs.WorkflowCancellationDialog, {
            title: this.i18N.tr('workflow-text'),
            cancel: () => {
                this.dialogClientService.hide();
            },
            submit: (cancellationReasonId, isReject, cancellationNotes) => {
                this.dialogClientService.hide();
                this.workflowAssignmentsService.cancelWorkflow(this.assignment.workflowStateId, cancellationReasonId, cancellationNotes).then(x => {
                    this.reload(x.workflowState, false, false);
                });
            }
        });
    }

    private async getCorrespondenceEmails() {

        //tries to get it from currentAssignment
        const currentMeta = JSON.parse(this.assignment?.meta ?? "{}");
        this.correspondenceEmails = currentMeta?.correspondenceEmails;

        //tries to get correspondenceEmails from previous assignments
        if (this.workflowState && this.assignment && (!this.correspondenceEmails || this.correspondenceEmails.length == 0)) {
            const previousAssignmentsMeta = this.workflowState.workflowAssignments?.filter(x => x.stepNumber < this.assignment.stepNumber).map(x => {
                return {
                    metaParsed: JSON.parse(x?.meta ?? "{}")
                }
            }) ?? [];

            previousAssignmentsMeta.forEach(x => {
                if (!this.correspondenceEmails && x?.metaParsed?.correspondenceEmails) {
                    this.correspondenceEmails = x?.metaParsed.correspondenceEmails;
                }
            })
        }

        //if it is still empty we get it from the vet
        if (!this.correspondenceEmails || this.correspondenceEmails.length == 0) {
            this.correspondenceEmails = this._model.item?.vetId
                ? (await this.vetClientService.getVetCorrespondenceEmails(this._model.item.vetId) ?? [])
                : [];
        }

    }

    public async getEmailLabel(email)  {
        if(!this.correspondenceEmails) {
            await this.getCorrespondenceEmails();

        }
        
        const emailMatches = this.correspondenceEmails?.filter(x => x.email == email) ?? [];
        //case when both ship manager and requestor email are the same
        if(emailMatches.length > 1) {
            return emailMatches.filter(x => x.isShipManager).length > 0? "(Ship Manager)" : emailMatches[0]?.recipientType
        }
        else {
            return emailMatches[0]?.recipientType
        }
        
    }

    onActivated() { }
}
